// Shared layout for the three placeholder legal pages: /policy, /terms,
// /data-policy. These three URLs need to be:
//
//   1. Publicly reachable (no HTTP basic auth) so OAuth providers
//      (Facebook/Google/Apple) can hit them during app review.
//   2. Stable in URL — once we register them as the privacy / ToS /
//      data-deletion URLs in the various developer consoles, changing
//      the path means re-submitting for review.
//
// The exemption from basic auth is enforced in nginx.conf, not here.
//
// Content note: the copy below is a STRUCTURAL placeholder. It covers
// the sections OAuth reviewers expect to see (data collected, lawful
// basis, retention, deletion path, contact) without making specific
// commitments that could later be wrong. Replace each <PlaceholderNote>
// with attorney-reviewed text before relying on these as the final
// public-facing legal text.

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import { nala } from '../components/nala/palette'

// Single source of truth used by the route components below — keeps
// the section copy version-controllable and lets a future rev replace
// the placeholder text section-by-section without touching layout.
interface LegalSection {
  heading: string
  body: ReactNode
}

interface LegalDoc {
  slug: 'policy' | 'terms' | 'data-policy'
  title: string
  description: string
  intro: ReactNode
  sections: LegalSection[]
}

// ─────────────────────────────────────────────────────────────────────────
// Public surface — three thin route wrappers exporting a default
// component each, so vite-react-ssg can prerender them statically.
// ─────────────────────────────────────────────────────────────────────────

export function PrivacyPolicyPage() {
  return <LegalLayout doc={PRIVACY_DOC} />
}

export function TermsPage() {
  return <LegalLayout doc={TERMS_DOC} />
}

export function DataPolicyPage() {
  return <LegalLayout doc={DATA_POLICY_DOC} />
}

// ─────────────────────────────────────────────────────────────────────────
// Layout — calm, document-style page that matches the editorial
// chrome. Renders inside EditorialLayout so the marketing header,
// floating SMS launcher, and booking modal are all available.
// ─────────────────────────────────────────────────────────────────────────

function LegalLayout({ doc }: { doc: LegalDoc }) {
  return (
    <>
      <SEOHead
        title={doc.title}
        description={doc.description}
        canonical={`https://nala.vet/${doc.slug}`}
      />

      <main
        style={{
          background: nala.bg,
          color: nala.ink,
          paddingTop: 56,
          paddingBottom: 96,
        }}
      >
        <article
          style={{
            maxWidth: 720,
            margin: '0 auto',
            padding: '0 24px',
          }}
        >
          {/* Title block */}
          <header style={{ marginBottom: 36 }}>
            <Link
              to="/"
              style={{
                color: nala.mute,
                fontSize: 13,
                textDecoration: 'none',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >
              ← Back to nala.vet
            </Link>
            <h1
              style={{
                fontFamily: nala.display,
                fontSize: 'clamp(34px, 5vw, 48px)',
                fontWeight: 500,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                marginTop: 18,
                marginBottom: 12,
              }}
            >
              {doc.title}
            </h1>
            <p
              style={{
                color: nala.mute,
                fontSize: 14,
                margin: 0,
              }}
            >
              Last updated: {LAST_UPDATED}
            </p>
          </header>

          <PlaceholderNote />

          <div
            style={{
              fontSize: 16,
              lineHeight: 1.7,
              color: nala.ink,
            }}
          >
            <div style={{ marginBottom: 28 }}>{doc.intro}</div>

            {doc.sections.map((s, i) => (
              <section key={i} style={{ marginTop: 32 }}>
                <h2
                  style={{
                    fontFamily: nala.display,
                    fontSize: 22,
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    marginBottom: 8,
                  }}
                >
                  {s.heading}
                </h2>
                <div>{s.body}</div>
              </section>
            ))}

            <section style={{ marginTop: 40 }}>
              <h2
                style={{
                  fontFamily: nala.display,
                  fontSize: 22,
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                  marginBottom: 8,
                }}
              >
                Contact
              </h2>
              <p>
                Questions about this {doc.slug === 'data-policy' ? 'data policy' : doc.slug === 'terms' ? 'agreement' : 'privacy policy'}?
                Email{' '}
                <a
                  href="mailto:support@nala.vet"
                  style={{ color: nala.accent, textDecoration: 'underline' }}
                >
                  support@nala.vet
                </a>{' '}
                or write to Nala Mobile Veterinary, c/o Privacy Officer, Miami, FL.
              </p>
            </section>
          </div>
        </article>
      </main>
    </>
  )
}

// Visible banner so anyone reading the page (including a Facebook
// reviewer) immediately understands these are stand-in documents and
// the production text is being prepared by counsel.
function PlaceholderNote() {
  return (
    <div
      style={{
        background: nala.soft,
        border: `1px solid ${nala.line}`,
        borderRadius: 12,
        padding: '14px 18px',
        fontSize: 14,
        lineHeight: 1.55,
        marginBottom: 28,
        color: nala.ink,
      }}
    >
      <strong style={{ fontWeight: 600 }}>Placeholder text.</strong>{' '}
      The final, attorney-reviewed version of this document is being prepared.
      The structure and commitments below reflect our current practices in
      good faith, but the binding text will be the version published here
      once finalised.
    </div>
  )
}

const LAST_UPDATED = 'May 5, 2026'

// ─────────────────────────────────────────────────────────────────────────
// Document content
// ─────────────────────────────────────────────────────────────────────────

const PRIVACY_DOC: LegalDoc = {
  slug: 'policy',
  title: 'Privacy Policy',
  description:
    'How Nala collects, uses, and protects your personal and pet health information.',
  intro: (
    <>
      <p style={{ margin: 0 }}>
        Nala ("we", "us", "our") provides in-home veterinary care to pet owners
        in Miami-Dade County. This policy explains what
        information we collect about you and your pet, why we collect it,
        how it is used, and the choices you have.
      </p>
    </>
  ),
  sections: [
    {
      heading: 'Information we collect',
      body: (
        <ul style={{ paddingLeft: 22, margin: 0 }}>
          <li><strong>Account &amp; contact:</strong> name, email, phone, address.</li>
          <li><strong>Pet records:</strong> species, breed, age, medical history, visit notes, prescriptions, vaccination history, photos taken during visits.</li>
          <li><strong>Payment:</strong> billing info processed by our PCI-compliant payment processor; we do not store full card numbers.</li>
          <li><strong>Communications:</strong> messages you send us via SMS, email, or the in-app chat.</li>
          <li><strong>Sign-in:</strong> if you use Sign in with Google, Apple, or Facebook, we receive your name, email, and a stable identifier; we never receive your social-network password.</li>
          <li><strong>Usage data:</strong> standard server logs, device type, IP address, and analytics events used to improve the product.</li>
        </ul>
      ),
    },
    {
      heading: 'How we use it',
      body: (
        <p>
          To deliver and document veterinary care, schedule visits, contact
          you about appointments, bill for services, send appointment
          reminders and follow-ups, comply with legal and recordkeeping
          obligations applicable to veterinary practices, and improve our
          services. We do not sell your personal information.
        </p>
      ),
    },
    {
      heading: 'Sharing',
      body: (
        <ul style={{ paddingLeft: 22, margin: 0 }}>
          <li>With the veterinarians and technicians on your visit team.</li>
          <li>With referral clinics, labs, and pharmacies, when needed for your pet's care.</li>
          <li>With service providers (hosting, SMS delivery, payments, mapping) under contract that limit their use of the data to providing those services.</li>
          <li>When required by law, subpoena, or to protect health, safety, or rights.</li>
        </ul>
      ),
    },
    {
      heading: 'Text messaging (SMS)',
      body: (
        <>
          <p style={{ marginTop: 0 }}>
            If you provide your mobile number and opt in, we send SMS about
            appointment scheduling, confirmations, reminders, follow-ups, and
            account or care updates. Message frequency varies. Message and data
            rates may apply. Reply STOP to unsubscribe at any time, or HELP for
            help. Consent to receive text messages is not a condition of
            purchasing any goods or services.
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>
              Your mobile information — including your phone number and SMS
              opt-in/consent — is never shared with third parties or affiliates
              for their own marketing or promotional purposes.
            </strong>{' '}
            We share your mobile number only with the messaging providers that
            deliver these texts on our behalf, under contracts that restrict
            them to providing that service, and where required by law. We do not
            sell your mobile opt-in data.
          </p>
        </>
      ),
    },
    {
      heading: 'Retention',
      body: (
        <p>
          Veterinary medical records are retained for the periods required
          by Florida and federal recordkeeping rules. Account data is kept
          while your account is active and for a reasonable archival period
          afterwards.
        </p>
      ),
    },
    {
      heading: 'Your rights',
      body: (
        <p>
          You can request a copy of the personal data we hold about you,
          request correction of inaccurate data, or request deletion subject
          to recordkeeping requirements that apply to medical records.
          See the <Link to="/data-policy" style={{ color: nala.accent }}>data policy</Link> for the deletion process.
        </p>
      ),
    },
    {
      heading: 'Cookies &amp; analytics',
      body: (
        <p>
          We use a small number of strictly necessary cookies for sign-in
          and security, plus aggregate analytics to understand how the
          site is used. We do not use third-party advertising trackers.
        </p>
      ),
    },
    {
      heading: 'Children',
      body: (
        <p>
          The service is intended for adults (18+) booking veterinary care
          for their own animals. We do not knowingly collect information
          from children.
        </p>
      ),
    },
    {
      heading: 'Changes',
      body: (
        <p>
          We may update this policy as the service evolves. The "last
          updated" date at the top reflects the most recent change;
          material changes will be flagged in-app.
        </p>
      ),
    },
  ],
}

const TERMS_DOC: LegalDoc = {
  slug: 'terms',
  title: 'Terms of Service',
  description:
    'The terms governing your use of Nala\u2019s house-call veterinary services and website.',
  intro: (
    <p style={{ margin: 0 }}>
      These terms govern your use of nala.vet and the in-home veterinary
      services we provide. By booking a visit or creating an account, you
      agree to these terms.
    </p>
  ),
  sections: [
    {
      heading: 'Services',
      body: (
        <p>
          Nala dispatches licensed veterinarians and technicians to your
          home for routine and sick care. We do not operate an emergency
          service: if your pet has a life-threatening condition, please
          call your nearest 24-hour veterinary hospital first.
        </p>
      ),
    },
    {
      heading: 'Eligibility',
      body: (
        <p>
          You must be 18 or older and the owner (or authorised handler) of
          the animal being treated. You agree to provide accurate
          information about yourself and your pet.
        </p>
      ),
    },
    {
      heading: 'Account &amp; security',
      body: (
        <p>
          You are responsible for keeping your sign-in credentials secure.
          Notify us promptly if you believe your account has been
          accessed without your permission.
        </p>
      ),
    },
    {
      heading: 'Booking, payment &amp; cancellations',
      body: (
        <ul style={{ paddingLeft: 22, margin: 0 }}>
          <li>Visits are billed at the rates quoted at booking; the trip fee covers travel and setup.</li>
          <li>Payment is collected at the end of the visit unless otherwise arranged.</li>
          <li>Cancellations made at least 4 hours before the visit window are free of charge; later cancellations or no-shows may be subject to a partial trip fee.</li>
        </ul>
      ),
    },
    {
      heading: 'Text messaging (SMS)',
      body: (
        <p>
          When you provide your mobile number and opt in, you agree to receive
          SMS from Nala about appointment scheduling, confirmations, reminders,
          follow-ups, and account or care updates. Consent is not a condition of
          purchasing any goods or services. Message frequency varies. Message
          and data rates may apply. Reply STOP to unsubscribe at any time, or
          HELP for help. How we handle your mobile information is described in
          our{' '}
          <Link to="/policy" style={{ color: nala.accent }}>Privacy Policy</Link>;
          in short, your phone number and SMS consent are never shared with
          third parties or affiliates for their marketing purposes.
        </p>
      ),
    },
    {
      heading: 'Veterinary judgment',
      body: (
        <p>
          Our veterinarians exercise their independent professional
          judgment in diagnosing and treating your pet. We may decline to
          provide a treatment we believe is unsafe or outside the scope
          of in-home care; in those cases we will refer you to an
          appropriate clinic or specialist.
        </p>
      ),
    },
    {
      heading: 'Acceptable use',
      body: (
        <p>
          Don't use the service to harass our staff, misrepresent your
          identity, or attempt to interfere with the operation of the
          site. We may suspend accounts that violate these basic rules.
        </p>
      ),
    },
    {
      heading: 'Liability',
      body: (
        <p>
          Nothing in these terms limits liability that cannot be limited
          under applicable law, including liability for personal injury
          caused by negligence or for fraud. Subject to that, our total
          liability for any claim arising out of the service is limited
          to the amount you paid us for the visit giving rise to the
          claim.
        </p>
      ),
    },
    {
      heading: 'Governing law',
      body: (
        <p>
          These terms are governed by the laws of the State of Florida.
          Disputes will be brought in the state or federal courts located
          in Miami-Dade County.
        </p>
      ),
    },
    {
      heading: 'Changes',
      body: (
        <p>
          We may update these terms; material changes will be flagged
          in-app and the new effective date will appear at the top of
          this page.
        </p>
      ),
    },
  ],
}

const DATA_POLICY_DOC: LegalDoc = {
  slug: 'data-policy',
  title: 'Data Policy &amp; Account Deletion',
  description:
    'How to delete your Nala account and the data we collect when you sign in with Google, Apple, or Facebook.',
  intro: (
    <p style={{ margin: 0 }}>
      This page exists for two purposes: (1) to provide the data-deletion
      instructions Facebook, Google, and Apple require us to publish for
      "Sign in with…" features, and (2) to summarise what we collect from
      those sign-in providers and how it is used. The full
      {' '}
      <Link to="/policy" style={{ color: nala.accent }}>privacy policy</Link>{' '}
      governs everything else.
    </p>
  ),
  sections: [
    {
      heading: 'What we receive from sign-in providers',
      body: (
        <ul style={{ paddingLeft: 22, margin: 0 }}>
          <li><strong>Google / Apple / Facebook:</strong> your name, email address, and the provider\u2019s stable user identifier. We do not receive your password, social-network friends list, posts, or contacts.</li>
          <li><strong>Apple specific:</strong> if you choose "Hide My Email", we receive a private relay address; we will only ever email you at the address Apple provides.</li>
          <li><strong>Facebook specific:</strong> only the public profile + email permissions; we do not request any other Facebook data.</li>
        </ul>
      ),
    },
    {
      heading: 'How to delete your account &amp; data',
      body: (
        <>
          <p style={{ marginTop: 0 }}>
            You can request full deletion of your Nala account and the
            associated personal data at any time. Two paths:
          </p>
          <ol style={{ paddingLeft: 22 }}>
            <li>
              <strong>Email:</strong> send a deletion request to{' '}
              <a href="mailto:support@nala.vet" style={{ color: nala.accent }}>support@nala.vet</a>{' '}
              from the email address tied to your account. We respond within 7 business days
              and complete the deletion within 30 days, with the exception noted below.
            </li>
            <li>
              <strong>In the app:</strong> sign in to the customer portal at{' '}
              <Link to="/portal" style={{ color: nala.accent }}>nala.vet/portal</Link>{' '}
              and use the "Delete my account" option in your profile (rolling out — until then, please use email).
            </li>
          </ol>
          <p>
            <strong>What we cannot delete:</strong> Florida law and federal recordkeeping rules
            require veterinary medical records to be retained for several years after the last
            visit. We will delete your contact details, sign-in identifiers, payment
            tokens, and analytics records, and we will redact medical records where the
            law allows. Records that must be retained will be kept on a restricted-access
            basis and disclosed only as required by law.
          </p>
        </>
      ),
    },
    {
      heading: 'Revoking provider access without deleting your account',
      body: (
        <ul style={{ paddingLeft: 22, margin: 0 }}>
          <li>
            <strong>Google:</strong>{' '}
            <a href="https://myaccount.google.com/permissions" style={{ color: nala.accent }} target="_blank" rel="noreferrer">myaccount.google.com/permissions</a>{' '}
            — find Nala in the list and remove access.
          </li>
          <li>
            <strong>Apple:</strong> Settings → [your name] → Sign In with Apple → Nala → Stop Using Apple ID.
          </li>
          <li>
            <strong>Facebook:</strong> Settings &amp; Privacy → Settings → Apps and Websites → Active → Nala → Remove.
          </li>
        </ul>
      ),
    },
    {
      heading: 'Data we share with third parties for sign-in',
      body: (
        <p>
          We do not share the data we receive from Google, Apple, or
          Facebook with third parties except (a) the service providers
          used to operate the platform (hosting, payments, etc., bound by
          confidentiality), and (b) when required by law.
        </p>
      ),
    },
  ],
}
